import React, { useState, useEffect } from 'react';
import { getBackendUrl } from '@/config';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Check, ChevronLeft, ChevronRight, Building2, Users, FileText, CreditCard, Clock, Shield, Upload, CheckCircle2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { llcAPI } from '@/services/api';
import { uploadFile } from '@/services/uploadService';
import { US_STATES, validateZipCode, handleAlphabeticInput, handleNumericInput } from '@/utils/validators';

const steps = [
  { id: 1, name: 'Package Selection', description: 'Choose your plan' },
  { id: 2, name: 'Business Details', description: 'Company information' },
  { id: 3, name: 'Registered Agent', description: 'Agent details' },
  { id: 4, name: 'Members/Owners', description: 'Ownership structure' },
  { id: 5, name: 'Upload Documents', description: 'Identity verification' },
  { id: 6, name: 'Review & Payment', description: 'Confirm details' },
];

export default function LLCCreationWizard() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  
  // Check if status allows editing - only DRAFT or NEED_INFO are editable
  const isEditableStatus = (status) => {
    return status === 'DRAFT' || status === 'draft' || status === 'NEED_INFO' || status === 'need_info';
  };
  
  const [currentStep, setCurrentStep] = useState(1);
  const [llcRecord, setLlcRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!editId);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    // Step 1
    selectedPackage: 'premium',
    // Step 2
    businessName: '',
    alternateNames: [],
    stateOfFormation: '',
    businessPurpose: '',
    businessAddress: '',
    businessCity: '',
    businessState: '',
    businessZip: '',
    // Step 3
    useOwnAgent: false,
    agentName: '',
    agentAddress: '',
    agentCity: '',
    agentState: '',
    agentZip: '',
    // Step 4
    managementStructure: 'member_managed',
    members: [],
    // Step 5 - Documents
    uploadedDocuments: [],
    otherDocuments: [], // Separate array for other documents
    // Step 6
    agreedToTerms: false
  });

  const progress = (currentStep / steps.length) * 100;

  // Load draft data if editing
  useEffect(() => {
    const loadDraft = async () => {
      if (!editId || !user) return;

      try {
        setLoading(true);
        const backendUrl = getBackendUrl();
        const token = localStorage.getItem('access_token');
        
        const response = await axios.get(`${backendUrl}/api/llc-formations/${editId}/detail`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const llcData = response.data;
        
        // Only allow editing if status is DRAFT or NEED_INFO
        if (!isEditableStatus(llcData.status)) {
          toast.error('Only Draft or Need Info forms can be edited');
          navigate('/llc/requests');
          return;
        }

        // Parse JSON fields
        const companyInfo = typeof llcData.company_info === 'string' 
          ? JSON.parse(llcData.company_info) 
          : llcData.company_info || {};
        
        const members = typeof llcData.members === 'string'
          ? JSON.parse(llcData.members)
          : llcData.members || {};
        
        const registeredAgent = typeof llcData.registered_agent === 'string'
          ? JSON.parse(llcData.registered_agent)
          : llcData.registered_agent || {};

        // Map backend data to form data
        setFormData({
          selectedPackage: companyInfo.selectedPackage || 'premium',
          businessName: llcData.company_name || '',
          alternateNames: companyInfo.alternateNames || [],
          stateOfFormation: llcData.state || '',
          businessPurpose: companyInfo.businessPurpose || '',
          businessAddress: companyInfo.address || '',
          businessCity: companyInfo.city || '',
          businessState: companyInfo.state || '',
          businessZip: companyInfo.zip || '',
          useOwnAgent: registeredAgent && Object.keys(registeredAgent).length > 0,
          agentName: registeredAgent.name || '',
          agentAddress: registeredAgent.address || '',
          agentCity: registeredAgent.city || '',
          agentState: registeredAgent.state || '',
          agentZip: registeredAgent.zip || '',
          managementStructure: members.managementStructure || 'member_managed',
          members: members.members || [],
          uploadedDocuments: companyInfo.uploadedDocuments || [],
          otherDocuments: companyInfo.otherDocuments || [],
          agreedToTerms: false
        });

        setLlcRecord(llcData);
        toast.success('Draft loaded successfully');
      } catch (error) {
        console.error('Error loading draft:', error);
        toast.error('Failed to load draft');
        navigate('/llc/requests');
      } finally {
        setLoading(false);
      }
    };

    loadDraft();
  }, [editId, user]);

  // Validation functions
  const validateStep = (step) => {
    const errors = {};

    switch (step) {
      case 2: // Business Details
        if (!formData.businessName || formData.businessName.trim().length < 3) {
          errors.businessName = 'Business name must be at least 3 characters';
        }
        if (!formData.stateOfFormation) {
          errors.stateOfFormation = 'State of Formation is required';
        }
        if (!formData.businessPurpose || formData.businessPurpose.trim().length < 5) {
          errors.businessPurpose = 'Business purpose must be at least 5 characters';
        }
        if (!formData.businessAddress || formData.businessAddress.trim().length < 5) {
          errors.businessAddress = 'Valid business address is required';
        }
        if (!formData.businessCity || formData.businessCity.trim().length < 2) {
          errors.businessCity = 'City is required';
        }
        if (!formData.businessState) {
          errors.businessState = 'State is required';
        }
        if (!formData.businessZip) {
          errors.businessZip = 'ZIP code is required';
        } else {
          const zipValidation = validateZipCode(formData.businessZip);
          if (!zipValidation.isValid) {
            errors.businessZip = zipValidation.error;
          }
        }
        break;

      case 3: // Registered Agent
        if (formData.useOwnAgent) {
          if (!formData.agentName || formData.agentName.trim().length < 2) {
            errors.agentName = 'Agent name is required';
          }
          if (!formData.agentAddress || formData.agentAddress.trim().length < 5) {
            errors.agentAddress = 'Agent address is required';
          }
          if (!formData.agentCity || formData.agentCity.trim().length < 2) {
            errors.agentCity = 'City is required';
          }
          if (!formData.agentState) {
            errors.agentState = 'State is required';
          }
          if (!formData.agentZip) {
            errors.agentZip = 'ZIP code is required';
          } else {
            const zipValidation = validateZipCode(formData.agentZip);
            if (!zipValidation.isValid) {
              errors.agentZip = zipValidation.error;
            }
          }
        }
        break;

      case 4: // Members
        if (formData.members.length === 0) {
          errors.members = 'At least one member/owner is required';
        } else {
          formData.members.forEach((member, index) => {
            if (!member.name || member.name.trim().length < 2) {
              errors[`member_${index}_name`] = 'Member name is required';
            }
            if (!member.address || member.address.trim().length < 5) {
              errors[`member_${index}_address`] = 'Member address is required';
            }
            if (!member.ownershipPercentage || parseFloat(member.ownershipPercentage) <= 0) {
              errors[`member_${index}_ownership`] = 'Valid ownership percentage is required';
            }
          });
          
          // Check total ownership adds to 100%
          const totalOwnership = formData.members.reduce((sum, m) => sum + (parseFloat(m.ownershipPercentage) || 0), 0);
          if (Math.abs(totalOwnership - 100) > 0.01) {
            errors.totalOwnership = `Total ownership must equal 100% (currently ${totalOwnership.toFixed(1)}%)`;
          }
        }
        break;

      case 6: // Review
        if (!formData.agreedToTerms) {
          errors.agreedToTerms = 'You must agree to the terms and conditions';
        }
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Save as draft to backend
  const saveDraft = async () => {
    try {
      setSaving(true);
      const llcData = {
        company_name: formData.businessName || 'Draft LLC',
        state: formData.stateOfFormation || 'CA',
        business_type: 'LLC',
        status: 'DRAFT',
        company_info: {
          selectedPackage: formData.selectedPackage,
          businessPurpose: formData.businessPurpose,
          businessAddress: formData.businessAddress,
          businessCity: formData.businessCity,
          businessState: formData.businessState,
          businessZip: formData.businessZip,
          alternateNames: formData.alternateNames,
          managementStructure: formData.managementStructure,
          uploadedDocuments: formData.uploadedDocuments || [],
          otherDocuments: formData.otherDocuments || [],
        },
        members: {
          managementStructure: formData.managementStructure,
          members: formData.members,
        },
        registered_agent: formData.useOwnAgent ? {
          useOwnAgent: formData.useOwnAgent,
          agentName: formData.agentName,
          agentAddress: formData.agentAddress,
          agentCity: formData.agentCity,
          agentState: formData.agentState,
          agentZip: formData.agentZip,
        } : null,
      };

      if (editId || llcRecord) {
        // Update existing draft
        const id = editId || llcRecord.id;
        await llcAPI.updateLLC(id, llcData);
        toast.success('Draft updated successfully');
        // Reload the updated data
        if (editId) {
          const response = await axios.get(`${getBackendUrl()}/api/llc-formations/${editId}/detail`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
          });
          setLlcRecord(response.data);
        }
      } else {
        // Create new draft
        const created = await llcAPI.createLLC(llcData);
        setLlcRecord(created);
        // Set editId so subsequent saves update instead of create
        if (created.id) {
          window.history.replaceState(null, '', `/llc/create?edit=${created.id}`);
        }
        toast.success('Draft created successfully');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error(error.response?.data?.detail || 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    // Validate current step before proceeding
    if (!validateStep(currentStep)) {
      toast.error('Please fix all validation errors before proceeding');
      return;
    }

    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    } else {
      // Final submission
      try {
        setSaving(true);
        const id = editId || llcRecord?.id;
        
        // Prepare LLC data
        const llcData = {
          company_name: formData.businessName || 'Draft LLC',
          state: formData.stateOfFormation || 'CA',
          business_type: 'LLC',
          status: 'SUBMITTED',
          company_info: {
            selectedPackage: formData.selectedPackage,
            businessPurpose: formData.businessPurpose,
            businessAddress: formData.businessAddress,
            businessCity: formData.businessCity,
            businessState: formData.businessState,
            businessZip: formData.businessZip,
            alternateNames: formData.alternateNames,
            managementStructure: formData.managementStructure,
            uploadedDocuments: formData.uploadedDocuments || [],
            otherDocuments: formData.otherDocuments || [],
          },
          members: {
            managementStructure: formData.managementStructure,
            members: formData.members,
          },
          registered_agent: formData.useOwnAgent ? {
            useOwnAgent: formData.useOwnAgent,
            agentName: formData.agentName,
            agentAddress: formData.agentAddress,
            agentCity: formData.agentCity,
            agentState: formData.agentState,
            agentZip: formData.agentZip,
          } : null,
        };
        
        if (id) {
          // Update existing LLC with all data and submit
          await llcAPI.updateLLC(id, llcData);
        } else {
          // Create new LLC with submitted status
          const response = await llcAPI.createLLC(llcData);
          // Store the new LLC id for document upload
          llcData.id = response.id;
          console.log('Created new LLC with ID:', response.id);
        }
        
        // Upload documents if any
        const llcId = id || llcData.id;
        
        console.log('LLC ID for upload:', llcId);
        console.log('Uploaded documents from formData:', formData.uploadedDocuments);
        
        // Upload documents using presigned URLs
        if (llcId && formData.uploadedDocuments && formData.uploadedDocuments.length > 0) {
          console.log('Starting document upload for', formData.uploadedDocuments.length, 'documents');
          let uploadCount = 0;
          
          for (const doc of formData.uploadedDocuments) {
            console.log('Processing doc:', {
              filename: doc.filename,
              hasFile: !!doc.file,
              fileType: doc.file ? doc.file.type : 'N/A',
              fileSize: doc.file ? doc.file.size : 'N/A',
              uploaded: doc.uploaded
            });
            
            if (doc.file && !doc.uploaded) {
              try {
                console.log('Uploading:', doc.filename, 'to LLC:', llcId);
                const result = await uploadFile(
                  doc.file,
                  'llc_creation',  // Use llc_creation to match DB enum
                  llcId,
                  doc.document_type || 'id_proof',  // Use id_proof for identity documents
                  (progress) => {
                    console.log(`Upload progress for ${doc.filename}: ${progress}%`);
                  }
                );
                
                if (!result.success) {
                  console.error('Upload failed for:', doc.filename, result.error);
                  toast.error(`Failed to upload ${doc.filename}: ${result.error}`);
                } else {
                  console.log('Successfully uploaded:', doc.filename, result.data);
                  uploadCount++;
                }
              } catch (docError) {
                console.error('Error uploading document:', doc.filename, docError);
                toast.error(`Failed to upload ${doc.filename}`);
              }
            } else {
              console.log('Skipping doc (no file or already uploaded):', doc.filename);
            }
          }
          
          if (uploadCount > 0) {
            toast.success(`Uploaded ${uploadCount} document(s) successfully`);
          }
        } else {
          console.log('No documents to upload. llcId:', llcId, 'docs:', formData.uploadedDocuments?.length || 0);
        }
        
        toast.success('LLC formation submitted successfully!');
        navigate('/llc/requests');
      } catch (error) {
        console.error('Error submitting LLC:', error);
        const errorMessage = error.response?.data?.detail || error.message || 'Failed to submit LLC formation';
        toast.error(errorMessage);
      } finally {
        setSaving(false);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const updateFormData = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear validation error for this field
    if (validationErrors[field]) {
      const newErrors = { ...validationErrors };
      delete newErrors[field];
      setValidationErrors(newErrors);
    }
  };

  const addMember = () => {
    const newMember = {
      id: Date.now(),
      name: '',
      address: '',
      ownershipPercentage: '',
      isManager: false
    };
    setFormData({
      ...formData,
      members: [...formData.members, newMember]
    });
  };

  const updateMember = (id, field, value) => {
    const updatedMembers = formData.members.map(member =>
      member.id === id ? { ...member, [field]: value } : member
    );
    setFormData({ ...formData, members: updatedMembers });
    
    // Clear member-specific errors
    const memberIndex = formData.members.findIndex(m => m.id === id);
    const errorKey = `member_${memberIndex}_${field}`;
    if (validationErrors[errorKey]) {
      const newErrors = { ...validationErrors };
      delete newErrors[errorKey];
      setValidationErrors(newErrors);
    }
  };

  const removeMember = (id) => {
    const updatedMembers = formData.members.filter(member => member.id !== id);
    setFormData({ ...formData, members: updatedMembers });
  };

  const packageDetails = {
    basic: { name: 'Basic', price: 99, turnaround: '5-7 days' },
    premium: { name: 'Premium', price: 249, turnaround: '2-3 days' },
    platinum: { name: 'Platinum', price: 499, turnaround: 'Same day' },
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="rounded-lg border-l-4 border-l-primary bg-primary/5 p-4">
              <p className="text-sm font-medium text-primary">Choose Your Package</p>
              <p className="text-sm text-muted-foreground mt-1">
                Select the formation package that best fits your needs
              </p>
            </div>

            <RadioGroup value={formData.selectedPackage} onValueChange={(value) => updateFormData('selectedPackage', value)}>
              <div className="grid gap-4 md:grid-cols-3">
                {Object.keys(packageDetails).map((pkg) => (
                  <label
                    key={pkg}
                    htmlFor={`package-${pkg}`}
                    className={cn(
                      'relative cursor-pointer rounded-lg border-2 p-6 transition-all',
                      formData.selectedPackage === pkg
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    )}
                  >
                    <RadioGroupItem value={pkg} id={`package-${pkg}`} className="sr-only" />
                    {pkg === 'premium' && (
                      <Badge className="absolute -top-2 right-4 bg-primary text-primary-foreground">
                        Popular
                      </Badge>
                    )}
                    <div className="text-center">
                      <p className="text-lg font-bold capitalize mb-2">{packageDetails[pkg].name}</p>
                      <p className="text-3xl font-bold text-primary mb-2">${packageDetails[pkg].price}</p>
                      <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        {packageDetails[pkg].turnaround}
                      </div>
                    </div>
                    {formData.selectedPackage === pkg && (
                      <div className="absolute top-4 right-4">
                        <div className="rounded-full bg-primary p-1">
                          <Check className="h-4 w-4 text-primary-foreground" />
                        </div>
                      </div>
                    )}
                  </label>
                ))}
              </div>
            </RadioGroup>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="rounded-lg border-l-4 border-l-primary bg-primary/5 p-4">
              <p className="text-sm font-medium text-primary">Business Information</p>
              <p className="text-sm text-muted-foreground mt-1">
                Provide your desired business details
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="businessName">Desired LLC Name *</Label>
              <Input
                id="businessName"
                value={formData.businessName}
                onChange={(e) => updateFormData('businessName', e.target.value)}
                placeholder="e.g., My Company LLC"
                className={validationErrors.businessName ? 'border-destructive' : ''}
              />
              {validationErrors.businessName && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {validationErrors.businessName}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                We'll check name availability in your selected state
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="stateOfFormation">State of Formation *</Label>
              <Select 
                value={formData.stateOfFormation} 
                onValueChange={(value) => updateFormData('stateOfFormation', value)}
              >
                <SelectTrigger 
                  id="stateOfFormation" 
                  className={validationErrors.stateOfFormation ? 'border-destructive' : ''}
                >
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DE">Delaware (Most Popular)</SelectItem>
                  <SelectItem value="WY">Wyoming (Tax Friendly)</SelectItem>
                  <SelectItem value="NV">Nevada (No State Tax)</SelectItem>
                  <SelectItem value="CA">California</SelectItem>
                  <SelectItem value="NY">New York</SelectItem>
                  <SelectItem value="TX">Texas</SelectItem>
                  <SelectItem value="FL">Florida</SelectItem>
                  {US_STATES.filter(s => !['DE', 'WY', 'NV', 'CA', 'NY', 'TX', 'FL'].includes(s.value)).map(state => (
                    <SelectItem key={state.value} value={state.value}>{state.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {validationErrors.stateOfFormation && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {validationErrors.stateOfFormation}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="businessPurpose">Business Purpose *</Label>
              <Input
                id="businessPurpose"
                value={formData.businessPurpose}
                onChange={(e) => updateFormData('businessPurpose', e.target.value)}
                placeholder="e.g., Professional consulting services"
                className={validationErrors.businessPurpose ? 'border-destructive' : ''}
              />
              {validationErrors.businessPurpose && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {validationErrors.businessPurpose}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="businessAddress">Principal Business Address *</Label>
              <Input
                id="businessAddress"
                value={formData.businessAddress}
                onChange={(e) => updateFormData('businessAddress', e.target.value)}
                placeholder="123 Main St"
                className={validationErrors.businessAddress ? 'border-destructive' : ''}
              />
              {validationErrors.businessAddress && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {validationErrors.businessAddress}
                </p>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="businessCity">City *</Label>
                <Input
                  id="businessCity"
                  value={formData.businessCity}
                  onChange={(e) => updateFormData('businessCity', handleAlphabeticInput(e.target.value))}
                  placeholder="San Francisco"
                  className={validationErrors.businessCity ? 'border-destructive' : ''}
                />
                {validationErrors.businessCity && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {validationErrors.businessCity}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="businessState">State *</Label>
                <Select 
                  value={formData.businessState} 
                  onValueChange={(value) => updateFormData('businessState', value)}
                >
                  <SelectTrigger 
                    id="businessState"
                    className={validationErrors.businessState ? 'border-destructive' : ''}
                  >
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((state) => (
                      <SelectItem key={state.value} value={state.value}>
                        {state.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {validationErrors.businessState && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {validationErrors.businessState}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="businessZip">ZIP Code *</Label>
                <Input
                  id="businessZip"
                  value={formData.businessZip}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/[^0-9-]/g, '');
                    const digitsOnly = cleaned.replace(/-/g, '');
                    if (digitsOnly.length <= 9) {
                      updateFormData('businessZip', cleaned);
                    }
                  }}
                  placeholder="12345 or 12345-6789"
                  maxLength={10}
                  className={validationErrors.businessZip ? 'border-destructive' : ''}
                />
                {validationErrors.businessZip && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {validationErrors.businessZip}
                  </p>
                )}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="rounded-lg border-l-4 border-l-primary bg-primary/5 p-4">
              <p className="text-sm font-medium text-primary">Registered Agent</p>
              <p className="text-sm text-muted-foreground mt-1">
                Every LLC needs a registered agent to receive legal documents
              </p>
            </div>

            <div className="flex items-start gap-3 rounded-lg border p-4">
              <Checkbox
                id="useOwnAgent"
                checked={formData.useOwnAgent}
                onCheckedChange={(checked) => updateFormData('useOwnAgent', checked)}
              />
              <div className="flex-1">
                <Label htmlFor="useOwnAgent" className="cursor-pointer font-medium">
                  I'll use my own registered agent
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  You must have a registered agent in the state of formation
                </p>
              </div>
            </div>

            {!formData.useOwnAgent ? (
              <Card className="border-primary/50 bg-primary/5">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
                    <h3 className="font-semibold text-foreground mb-2">Kosha Registered Agent Service</h3>
                    <p className="text-sm text-muted-foreground">
                      We'll serve as your registered agent (included in Premium & Platinum packages)
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="agentName">Agent Name *</Label>
                  <Input
                    id="agentName"
                    value={formData.agentName}
                    onChange={(e) => updateFormData('agentName', e.target.value)}
                    className={validationErrors.agentName ? 'border-destructive' : ''}
                  />
                  {validationErrors.agentName && (
                    <p className="text-sm text-destructive flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {validationErrors.agentName}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="agentAddress">Agent Address *</Label>
                  <Input
                    id="agentAddress"
                    value={formData.agentAddress}
                    onChange={(e) => updateFormData('agentAddress', e.target.value)}
                    className={validationErrors.agentAddress ? 'border-destructive' : ''}
                  />
                  {validationErrors.agentAddress && (
                    <p className="text-sm text-destructive flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {validationErrors.agentAddress}
                    </p>
                  )}
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="agentCity">City *</Label>
                    <Input
                      id="agentCity"
                      value={formData.agentCity}
                      onChange={(e) => updateFormData('agentCity', handleAlphabeticInput(e.target.value))}
                      placeholder="San Francisco"
                      className={validationErrors.agentCity ? 'border-destructive' : ''}
                    />
                    {validationErrors.agentCity && (
                      <p className="text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" />
                        {validationErrors.agentCity}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="agentState">State *</Label>
                    <Select 
                      value={formData.agentState} 
                      onValueChange={(value) => updateFormData('agentState', value)}
                    >
                      <SelectTrigger 
                        id="agentState"
                        className={validationErrors.agentState ? 'border-destructive' : ''}
                      >
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map((state) => (
                          <SelectItem key={state.value} value={state.value}>
                            {state.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {validationErrors.agentState && (
                      <p className="text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" />
                        {validationErrors.agentState}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="agentZip">ZIP Code *</Label>
                    <Input
                      id="agentZip"
                      value={formData.agentZip}
                      onChange={(e) => {
                        const cleaned = e.target.value.replace(/[^0-9-]/g, '');
                        const digitsOnly = cleaned.replace(/-/g, '');
                        if (digitsOnly.length <= 9) {
                          updateFormData('agentZip', cleaned);
                        }
                      }}
                      placeholder="12345 or 12345-6789"
                      maxLength={10}
                      className={validationErrors.agentZip ? 'border-destructive' : ''}
                    />
                    {validationErrors.agentZip && (
                      <p className="text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" />
                        {validationErrors.agentZip}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="rounded-lg border-l-4 border-l-primary bg-primary/5 p-4">
              <p className="text-sm font-medium text-primary">Ownership Structure</p>
              <p className="text-sm text-muted-foreground mt-1">
                Define who owns and manages the LLC
              </p>
            </div>

            <div className="space-y-2">
              <Label>Management Structure *</Label>
              <RadioGroup value={formData.managementStructure} onValueChange={(value) => updateFormData('managementStructure', value)}>
                <div className="flex items-center space-x-2 rounded-lg border p-4">
                  <RadioGroupItem value="member_managed" id="member_managed" />
                  <div className="flex-1">
                    <Label htmlFor="member_managed" className="cursor-pointer font-medium">
                      Member-Managed
                    </Label>
                    <p className="text-sm text-muted-foreground">All members participate in daily operations</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 rounded-lg border p-4">
                  <RadioGroupItem value="manager_managed" id="manager_managed" />
                  <div className="flex-1">
                    <Label htmlFor="manager_managed" className="cursor-pointer font-medium">
                      Manager-Managed
                    </Label>
                    <p className="text-sm text-muted-foreground">Designated managers run daily operations</p>
                  </div>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Members/Owners</Label>
                <Button type="button" onClick={addMember} variant="outline" size="sm">
                  + Add Member
                </Button>
              </div>

              {validationErrors.members && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {validationErrors.members}
                </p>
              )}
              {validationErrors.totalOwnership && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {validationErrors.totalOwnership}
                </p>
              )}

              {formData.members.length > 0 ? (
                <div className="space-y-4">
                  {formData.members.map((member, index) => (
                    <Card key={member.id} className="tax-card">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base">Member {index + 1}</CardTitle>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeMember(member.id)}
                          >
                            Remove
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <Label>Full Name *</Label>
                          <Input
                            value={member.name}
                            onChange={(e) => updateMember(member.id, 'name', e.target.value)}
                            placeholder="John Doe"
                            className={validationErrors[`member_${index}_name`] ? 'border-destructive' : ''}
                          />
                          {validationErrors[`member_${index}_name`] && (
                            <p className="text-sm text-destructive flex items-center gap-1">
                              <AlertCircle className="h-4 w-4" />
                              {validationErrors[`member_${index}_name`]}
                            </p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label>Address *</Label>
                          <Input
                            value={member.address}
                            onChange={(e) => updateMember(member.id, 'address', e.target.value)}
                            placeholder="123 Main St, City, State ZIP"
                            className={validationErrors[`member_${index}_address`] ? 'border-destructive' : ''}
                          />
                          {validationErrors[`member_${index}_address`] && (
                            <p className="text-sm text-destructive flex items-center gap-1">
                              <AlertCircle className="h-4 w-4" />
                              {validationErrors[`member_${index}_address`]}
                            </p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label>Ownership Percentage *</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={member.ownershipPercentage}
                            onChange={(e) => updateMember(member.id, 'ownershipPercentage', e.target.value)}
                            placeholder="25.0"
                            className={validationErrors[`member_${index}_ownership`] ? 'border-destructive' : ''}
                          />
                          {validationErrors[`member_${index}_ownership`] && (
                            <p className="text-sm text-destructive flex items-center gap-1">
                              <AlertCircle className="h-4 w-4" />
                              {validationErrors[`member_${index}_ownership`]}
                            </p>
                          )}
                        </div>
                        {formData.managementStructure === 'manager_managed' && (
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id={`manager-${member.id}`}
                              checked={member.isManager}
                              onCheckedChange={(checked) => updateMember(member.id, 'isManager', checked)}
                            />
                            <Label htmlFor={`manager-${member.id}`} className="cursor-pointer">
                              Designated as Manager
                            </Label>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="border-dashed">
                  <CardContent className="pt-6">
                    <div className="text-center py-8">
                      <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-sm text-muted-foreground">No members added yet</p>
                      <Button type="button" onClick={addMember} variant="outline" className="mt-4">
                        + Add First Member
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="rounded-lg border-l-4 border-l-primary bg-primary/5 p-4">
              <p className="text-sm font-medium text-primary">Upload Documents</p>
              <p className="text-sm text-muted-foreground mt-1">
                Upload identification documents for all members (optional but recommended)
              </p>
            </div>
            
            {/* Document Types Guide */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Required Documents</CardTitle>
                <CardDescription>Upload the following documents for faster processing</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex items-start gap-3 p-3 rounded-lg border">
                    <FileText className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Government ID</p>
                      <p className="text-xs text-muted-foreground">Driver's license, passport, or state ID</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg border">
                    <FileText className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Proof of Address</p>
                      <p className="text-xs text-muted-foreground">Utility bill or bank statement</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Upload Area */}
            <Card className="border-dashed">
              <CardHeader>
                <CardTitle className="text-base">Required Identity Documents</CardTitle>
                <CardDescription>Government ID and proof of address for verification</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div 
                    className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary transition-colors cursor-pointer"
                    onClick={() => document.getElementById('llc-doc-upload').click()}
                  >
                    <input
                      id="llc-doc-upload"
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png"
                      multiple
                      onChange={async (e) => {
                        const files = Array.from(e.target.files);
                        if (files.length === 0) return;
                        
                        for (const file of files) {
                          // Validate file size (10MB max)
                          if (file.size > 10 * 1024 * 1024) {
                            toast.error(`${file.name} is too large. Max size is 10MB`);
                            continue;
                          }
                          
                          // Add to local state first
                          const newDoc = {
                            id: `temp-${Date.now()}-${Math.random()}`,
                            filename: file.name,
                            document_type: 'id_proof',  // Valid DB enum value
                            size: file.size,
                            file: file,
                            uploading: false,
                            uploaded: false
                          };
                          
                          updateFormData('uploadedDocuments', [...formData.uploadedDocuments, newDoc]);
                        }
                        e.target.value = '';
                      }}
                    />
                    <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm font-medium">Drop files here or click to upload</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      PDF, JPG, PNG up to 10MB each
                    </p>
                  </div>
                  
                  {/* Uploaded Documents List */}
                  {formData.uploadedDocuments.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Uploaded Documents ({formData.uploadedDocuments.length})</p>
                      {formData.uploadedDocuments.map((doc, index) => (
                        <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg border bg-muted/50">
                          <div className="flex items-center gap-3">
                            <FileText className="h-5 w-5 text-primary" />
                            <div>
                              <p className="text-sm font-medium">{doc.filename}</p>
                              <p className="text-xs text-muted-foreground">
                                {(doc.size / 1024).toFixed(1)} KB
                                {doc.uploaded && <span className="text-green-600 ml-2">✓ Uploaded</span>}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => {
                              const newDocs = formData.uploadedDocuments.filter((_, i) => i !== index);
                              updateFormData('uploadedDocuments', newDocs);
                            }}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Other Documents Section */}
            <Card className="border-dashed">
              <CardHeader>
                <CardTitle className="text-base">Other Supporting Documents</CardTitle>
                <CardDescription>Upload any additional documents that support your LLC formation</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div 
                    className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary transition-colors cursor-pointer bg-muted/30"
                    onClick={() => document.getElementById('llc-other-docs-upload').click()}
                  >
                    <input
                      id="llc-other-docs-upload"
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png"
                      multiple
                      onChange={async (e) => {
                        const files = Array.from(e.target.files);
                        if (files.length === 0) return;
                        
                        for (const file of files) {
                          // Validate file size (10MB max)
                          if (file.size > 10 * 1024 * 1024) {
                            toast.error(`${file.name} is too large. Max size is 10MB`);
                            continue;
                          }
                          
                          // Add to other documents array
                          const newDoc = {
                            id: `other-${Date.now()}-${Math.random()}`,
                            filename: file.name,
                            document_type: 'other',
                            size: file.size,
                            file: file,
                            uploading: false,
                            uploaded: false
                          };
                          
                          updateFormData('otherDocuments', [...(formData.otherDocuments || []), newDoc]);
                        }
                        e.target.value = '';
                        toast.success(`${files.length} file(s) added`);
                      }}
                    />
                    <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm font-medium">Click to upload additional documents</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Business plans, contracts, or other supporting materials
                    </p>
                  </div>
                  
                  {/* Other Documents List */}
                  {formData.otherDocuments && formData.otherDocuments.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Other Documents ({formData.otherDocuments.length})</p>
                      {formData.otherDocuments.map((doc, index) => (
                        <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg border bg-muted/50">
                          <div className="flex items-center gap-3">
                            <FileText className="h-5 w-5 text-primary" />
                            <div>
                              <p className="text-sm font-medium">{doc.filename}</p>
                              <p className="text-xs text-muted-foreground">
                                {(doc.size / 1024).toFixed(1)} KB
                                {doc.uploaded && <span className="text-green-600 ml-2">✓ Uploaded</span>}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => {
                              const newDocs = formData.otherDocuments.filter((_, i) => i !== index);
                              updateFormData('otherDocuments', newDocs);
                              toast.success('Document removed');
                            }}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            
            <p className="text-xs text-muted-foreground text-center">
              Documents will be uploaded when you submit your LLC formation. You can also upload documents later from your dashboard.
            </p>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div className="rounded-lg border-l-4 border-l-primary bg-primary/5 p-4">
              <p className="text-sm font-medium text-primary">Review & Submit</p>
              <p className="text-sm text-muted-foreground mt-1">
                Review your information and submit your LLC formation
              </p>
            </div>

            {/* Summary of information */}
            <Card>
              <CardHeader>
                <CardTitle>Business Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Business Name:</span>
                  <span className="font-medium">{formData.businessName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">State:</span>
                  <span className="font-medium">{formData.stateOfFormation}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Package:</span>
                  <span className="font-medium capitalize">{formData.selectedPackage}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Members:</span>
                  <span className="font-medium">{formData.members.length}</span>
                </div>
              </CardContent>
            </Card>

            <div className="flex items-start gap-3 rounded-lg border p-4">
              <Checkbox
                id="agreedToTerms"
                checked={formData.agreedToTerms}
                onCheckedChange={(checked) => updateFormData('agreedToTerms', checked)}
              />
              <div className="flex-1">
                <Label htmlFor="agreedToTerms" className="cursor-pointer font-medium">
                  I agree to the terms and conditions *
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  By checking this box, you agree to our terms of service and privacy policy
                </p>
              </div>
            </div>
            {validationErrors.agreedToTerms && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {validationErrors.agreedToTerms}
              </p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <DashboardLayout role={user.role} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="ml-4 text-muted-foreground">Loading draft...</p>
        </div>
      </DashboardLayout>
    );
  }

  // Show loading state if auth is still loading or user is not loaded yet
  if (authLoading || !user) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <DashboardLayout role={user.role} user={user} title="LLC Formation">
      <div className="mx-auto max-w-4xl space-y-6">
        <Breadcrumbs items={[
          { label: 'LLC Dashboard', href: '/llc/dashboard' },
          { label: editId ? 'Edit Draft' : 'New Formation', href: '/llc/create' }
        ]} />

        <div>
          <h1 className="text-3xl font-bold text-foreground">{editId ? 'Edit LLC Draft' : 'LLC Formation Wizard'}</h1>
          <p className="mt-2 text-muted-foreground">
            Complete all steps to register your Limited Liability Company
          </p>
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Step {currentStep} of {steps.length}</span>
            <span className="text-muted-foreground">{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Steps Navigation */}
        <div className="grid gap-2 grid-cols-3 md:grid-cols-6">
          {steps.map((step) => (
            <div
              key={step.id}
              className={cn(
                'flex flex-col items-center gap-2 rounded-lg border p-3 transition-all min-w-0',
                currentStep === step.id
                  ? 'border-primary bg-primary/5'
                  : currentStep > step.id
                  ? 'border-green-500 bg-green-50 dark:bg-green-950'
                  : 'border-border bg-card'
              )}
            >
              <div
                className={cn(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
                  currentStep === step.id
                    ? 'bg-primary text-primary-foreground'
                    : currentStep > step.id
                    ? 'bg-green-500 text-white'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                {currentStep > step.id ? <Check className="h-4 w-4" /> : step.id}
              </div>
              <div className="hidden sm:block text-center w-full">
                <p className="text-xs font-medium leading-tight break-words">{step.name}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Form Content */}
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle>{steps[currentStep - 1].name}</CardTitle>
            <CardDescription>{steps[currentStep - 1].description}</CardDescription>
          </CardHeader>
          <CardContent>
            {renderStepContent()}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 1}
          >
            <ChevronLeft className="mr-2 h-4 w-4" />
            Previous
          </Button>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={saveDraft}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Draft'}
            </Button>
            <Button
              type="button"
              onClick={handleNext}
              disabled={saving}
            >
              {currentStep === steps.length ? 'Submit' : 'Next'}
              {currentStep < steps.length && <ChevronRight className="ml-2 h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
